import styled, {css} from 'styled-components';
import {Avatar as UserAvatar, Button, InformationTile, StateColor, Typography} from '@innowise-group/ui-kit';
import {Title as TileTitle, Value} from '../../../../shared/ui-kit/information-tile/information-tile.styles';
import {Avatar} from '../../../../shared/ui-kit/avatar/avatar.styles';

interface WorkspaceViewContainerProps {
  selected: boolean;
  themeColor: StateColor;
  rotateDegree: number;
  isViewMode: boolean;
  isDraft?: boolean;
}
export const WorkspaceViewContainer = styled.div<WorkspaceViewContainerProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100px;
  height: 50px;
  transform: ${({rotateDegree}) => `rotate(${rotateDegree}deg)`};
  border-radius: 6px;
  transition: 0.2s;
  cursor: ${({isViewMode}) => (isViewMode ? 'pointer' : 'grab')};

  ${({selected, themeColor, isDraft, theme}) => {
    if (isDraft) {
      return css`
        background-color: ${theme.palette.general.lightGrey[80]};
        border: 1px dashed ${theme.palette.general.darkGrey[60]};
        box-sizing: border-box;
        animation: workspace-draft-pulse 1.4s ease-in-out infinite;
        pointer-events: auto;

        @keyframes workspace-draft-pulse {
          0%,
          100% {
            opacity: 1;
            box-shadow: 0 0 0 0 rgba(120, 120, 120, 0.35);
          }
          50% {
            opacity: 0.55;
            box-shadow: 0 0 0 6px rgba(120, 120, 120, 0);
          }
        }
      `;
    }

    return css`
      background-color: ${selected ? themeColor.default : themeColor.backgroundSecondary};
      border: ${selected ? `1px solid ${themeColor.pressed}` : 'none'};
      box-sizing: border-box;
      &:hover {
        background-color: ${themeColor.hover};
      }
    `;
  }}
`;

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100px;
  height: 50px;
`;

export const Title = styled(Typography)`
  font-size: 10px;
`;

export const SavingSpinner = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid ${({theme}) => theme.palette.general.lightGrey[60]};
  border-top-color: ${({theme}) => theme.palette.general.smart};
  animation: workspace-saving-spin 0.7s linear infinite;

  @keyframes workspace-saving-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const Subtitle = styled(Typography)`
  width: inherit;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
`;

export const WorkspaceActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const WorkspaceActionsBlock = styled.div`
  display: flex;

  & > * {
    margin-right: 8px;

    &:last-child {
      margin-right: 0;
    }
  }
`;

export const RoomLabel = styled(Typography)`
  font-size: 12px;
  margin-bottom: 6px;
  white-space: nowrap;
`;

export const IconsContainer = styled.div`
  display: flex;
  align-items: center;

  & > * {
    margin-right: 10px;

    &:last-child {
      margin-right: 0;
    }
  }
`;

export const WorkspaceInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 210px;
  max-width: 290px;
  box-sizing: border-box;
`;

export const BookingsScrollList = styled.div`
  max-height: 220px;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 4px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const WorkspaceStatusBlock = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 4px;
`;

export const WorkspaceStatusText = styled(Typography)<{textColor: string}>`
  margin-left: 5px;
  color: ${({textColor}) => textColor};
`;

export const BookingInfoContainer = styled.div`
  border-top: 1px solid ${({theme}) => theme.palette.general.lightGrey[60]};
  padding: 5px 0 8px;
  &:last-child {
    padding: 5px 0;
  }
`;

export const WorkspaceEmployeeBlock = styled(WorkspaceStatusBlock)``;

export const BookingPeriodBlock = styled(WorkspaceEmployeeBlock)``;

export const BookingPeriodText = styled(Typography)`
  margin-left: 5px;
`;

export const UserPhoto = styled(UserAvatar)<{margin?: number; draggable?: boolean; isPointer?: boolean}>`
  ${Avatar} {
    pointer-events: ${({draggable = false}) => (draggable ? 'all' : 'none')};
  }
  margin-right: ${({margin = 10}) => `${margin}px`};
  cursor: ${({draggable = false, isPointer = false}) => (draggable ? 'grab' : isPointer ? 'pointer' : 'default')};
`;

export const BookButton = styled(Button)`
  margin-top: 10px;
`;

export const Tile = styled(InformationTile)`
  padding: 4px 0;

  &:last-child {
    padding-bottom: 0;
  }

  ${TileTitle} {
    font-size: 12px;
  }

  ${Value} {
    font-size: 12px;
  }
`;

export const ViewAttendanceBtn = styled(Button)`
  color: ${({theme}) => theme.palette.generalAlt.font.tertiary};
  border: 1px solid ${({theme}) => theme.palette.generalAlt.font.tertiary};
  transition: 0.2s ease-in-out all;
  &:hover {
    box-shadow: 0 0 0 1px ${({theme}) => theme.palette.generalAlt.font.tertiary};
    font-weight: 600;
    transition: 0.2s ease-in-out all;
  }
`;

interface AttendanceBadgeProps {
  backgroundColor: string;
}

export const AttendanceBadge = styled.div<AttendanceBadgeProps>`
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid ${({theme}) => theme.palette.general.white};
  background-color: ${({backgroundColor}) => backgroundColor};
  box-shadow: 0px 0px 12px 0px #00000073;
  z-index: 1;

  & > p {
    font-size: 8px;
    line-height: 1;
    font-weight: 800;
    color: ${({theme}) => theme.palette.general.white};
    & > span {
      font-size: 5px;
      line-height: 1;
    }
  }
`;
